/**
 * Telefon / tarayici bildirimleri (Web Push) - istemci tarafi.
 *
 * Akis: service worker kaydi (public/sw.js) -> kullanici "Bildirimleri ac"
 * der -> tarayici izin sorar -> sunucunun acik anahtariyla abone olunur ->
 * abonelik sunucuya kaydedilir (kullanici basina, bkz. backend
 * scanner/watchlist.py PushSubscription). Sunucu alarm/favori olaylarinda
 * bu cihaza push gonderir; site kapaliyken de gelir.
 *
 * iPHONE: Apple push'u normal Safari sekmesine VERMEZ; yalnizca "Paylas ->
 * Ana Ekrana Ekle" ile kurulan uygulamada (iOS 16.4+) calisir. Bu durum
 * "needs-install" olarak ayrica bildirilir ki arayuz kullaniciyi yonlendirsin.
 */

import { api } from "../api/client";

export type PushState =
  /** Bu tarayici/cihaz push desteklemiyor (ör. eski iOS, http:// yerel ag). */
  | "unsupported"
  /** iPhone/iPad, Safari sekmesinde: once Ana Ekrana Ekle gerekli. */
  | "needs-install"
  /** Kullanici izni reddetmis - yalnizca tarayici ayarlarindan geri acilir. */
  | "denied"
  | "off"
  | "on";

export function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPadOS 13+ kendini Mac olarak tanitir; dokunmatik olmasi ele verir.
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.("(display-mode: standalone)").matches || nav.standalone === true;
}

/** Ayarlar'da "hangi cihazlarda acik" bilgisi icin kaba etiket. */
export function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1)) return "iPad";
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? "Android telefon" : "Android tablet";
  if (/Windows/.test(ua)) return "Windows";
  if (/Macintosh/.test(ua)) return "Mac";
  return "Tarayıcı";
}

function supported(): boolean {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined"
  );
}

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

/** Service worker'i bir kez kaydeder. Desteklenmiyorsa null (hata YOK). */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!registration) {
    registration =
      typeof window !== "undefined" && window.isSecureContext && "serviceWorker" in navigator
        ? navigator.serviceWorker.register("/sw.js").catch(() => null)
        : Promise.resolve(null);
  }
  return registration;
}

export async function getPushState(): Promise<PushState> {
  if (isIos() && !isStandalone()) return "needs-install";
  if (!supported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = await registerServiceWorker();
  if (!reg) return "unsupported";
  const subscription = await reg.pushManager.getSubscription();
  return subscription ? "on" : "off";
}

function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padded = value + "=".repeat((4 - (value.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
}

/** Izin ister, abone olur, sunucuya kaydeder. Hata mesaji Turkcedir. */
export async function enablePush(): Promise<void> {
  if (!supported()) throw new Error("Bu cihaz veya tarayıcı bildirimleri desteklemiyor.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Bildirim izni reddedildi. Tarayıcı ayarlarından bu site için izin verebilirsin."
        : "Bildirim izni verilmedi.",
    );
  }
  const reg = await registerServiceWorker();
  if (!reg) throw new Error("Bildirim altyapısı başlatılamadı.");
  const { public_key } = await api.pushConfig();
  const key = urlBase64ToUint8Array(public_key);
  let subscription = await reg.pushManager.getSubscription();
  // Sunucunun anahtari degismisse eski abonelik ise yaramaz - yenilenir.
  if (subscription && subscription.options.applicationServerKey) {
    const current = new Uint8Array(subscription.options.applicationServerKey);
    if (current.length !== key.length || current.some((byte, index) => byte !== key[index])) {
      await subscription.unsubscribe();
      subscription = null;
    }
  }
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key,
    });
  }
  const json = subscription.toJSON();
  await api.pushSubscribe({
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
    device: deviceLabel(),
  });
}

export async function disablePush(): Promise<void> {
  const reg = await registerServiceWorker();
  const subscription = reg ? await reg.pushManager.getSubscription() : null;
  if (!subscription) return;
  try {
    await api.pushUnsubscribe(subscription.endpoint);
  } finally {
    await subscription.unsubscribe();
  }
}
